import { useTranslation } from 'next-i18next';
import React, { useContext } from 'react';
import Tooltip from 'react-tooltip-lite';

import styles from '@/shared/components/molecules/JobInstanceCard/JobInstanceCard.module.css';

import { getCardData } from '@/modules/workingDayReconciliation/components/CrewLevel/DetailedCard/logic';
import ChooseModal from '@/modules/workingDayReconciliation/components/CrewLevel/DetailedCard/Modal/ChooseModal';
import { WorkingDayReconciliationContext } from '@/modules/workingDayReconciliation/context/context';
import { workingDayReconciliationActionsTypes } from '@/modules/workingDayReconciliation/context/reducer';
import { IReconciliation } from '@/modules/workingDayReconciliation/types';
import { Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import DotedMenu from '@/shared/components/molecules/DotedMenu';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import ExleminationMark from '~/images/shared/ExleminationMark.svg';
import Person from '~/images/shared/Person.svg';

interface Props {
  data: IReconciliation.IGetWorkdaySummary;
}

const CardContent: React.FC<Props> = ({ data }) => {
  // TODO: UPDATE CONTENT WHEN ID CHANGE
  const {
    dispatch,
    state: { currentSupplierSummary },
  } = useContext(WorkingDayReconciliationContext);
  const { openModal } = useModal();
  const [store] = useStore((store) => store);
  const isDemoFarm = store?.isDemoFarm;
  const selectedAsset = store?.asset;

  const setCurrentSupplierSummary = () =>
    dispatch({
      type: workingDayReconciliationActionsTypes.setCurrentSupplierSummary,
      payload: data,
    });

  const { t } = useTranslation('common');
  const cardData = getCardData({
    data,
    currency: store?.asset?.currency || '',
  });

  const menuList = [
    {
      title: t('deduction-details'),
      id: 'view_deduction_details',
      action: () =>
        openModal(
          isDemoFarm ? (
            <DemoCTA />
          ) : (
            <ChooseModal
              currentSupplierSummary={currentSupplierSummary}
              modalType='deduction'
              operationType='view'
            />
          ),
          {
            title: t('deduction-details'),
          }
        ),
    },
    {
      title: t('bonuses-details'),
      id: 'view_bonuses_details',
      action: () =>
        openModal(
          isDemoFarm ? (
            <DemoCTA />
          ) : (
            <ChooseModal
              currentSupplierSummary={currentSupplierSummary}
              modalType='bonus'
              operationType='view'
            />
          ),
          {
            title: t('bonuses-details'),
          }
        ),
    },
    // TODO: ic of what
    {
      title:
        selectedAsset?.settlementLevel === 2 ? (
          <TippyTooltip
            placement='top'
            content={t('add-bonus-settlement-level')}
            maxWidth={280}
          >
            <span className='opacity-3'>{t('add-bonus')}</span>
          </TippyTooltip>
        ) : (
          t('add-bonus')
        ),
      id: 'open_add_bonus',
      action: () =>
        isDemoFarm
          ? openModal(<DemoCTA />)
          : selectedAsset?.settlementLevel === 1
          ? openModal(
              <ChooseModal
                currentSupplierSummary={currentSupplierSummary}
                modalType='bonus'
                operationType='add'
              />,
              {
                title: t('add-bonus'),
              }
            )
          : undefined,
    },
    // TODO: ic of what??
    {
      title:
        selectedAsset?.settlementLevel === 2 ? (
          <TippyTooltip
            placement='top'
            content={t('add-deduction-settlement-level')}
            maxWidth={280}
          >
            <span className='opacity-3'>{t('add-deduction')}</span>
          </TippyTooltip>
        ) : (
          t('add-deduction')
        ),
      id: 'open_add_deduction',
      action: () =>
        isDemoFarm
          ? openModal(<DemoCTA />)
          : selectedAsset?.settlementLevel === 1
          ? openModal(
              <ChooseModal
                currentSupplierSummary={currentSupplierSummary}
                modalType='deduction'
                operationType='add'
              />,
              {
                title: t('add-deduction'),
              }
            )
          : undefined,
    },
  ];

  return (
    <div className='flex w-full items-center gap-2'>
      <div className={styles['imageWrapper']}>
        <span className='text-24'>
          <Person />
        </span>
      </div>
      <div className='flex w-full items-center justify-between gap-12  overflow-x-auto whitespace-nowrap py-16 overflow-y-hidden scrollbar-thin scrollbar-thumb-background-disable scrollbar-thumb-rounded-lg scrollbar-w-5 scrollbar-h-5'>
        {Object.entries(cardData).map(([key, value]) => (
          <div key={key} className='max-w-1/5 flex flex-col gap-1'>
            <div className='w-fit'>
              <Typography variant='body2' className='text-text-600'>
                {t(key)}
              </Typography>
              <Typography
                variant='subtitle2'
                className={` relative ${
                  key === 'issuedReceivables' && value.status === 2
                    ? 'text-orange-700'
                    : key === 'issuedReceivables' && value.status === 1
                    ? 'text-states-success'
                    : 'text-text-default'
                }`}
                weight={
                  key === 'issuedReceivables' || key === 'netDues'
                    ? 'bold'
                    : 'regular'
                }
              >
                {key === 'issuedReceivables' ? (
                  value?.status == 1 || value?.status == 0 ? (
                    value.text
                  ) : (
                    <Tooltip
                      content={
                        <div className='w-fit min-w-[200px] rounded-lg bg-background-200 px-16 py-4 text-center'>
                          {t('not-issued-receivables')}
                        </div>
                      }
                      direction='down'
                      tagName='div'
                      tipContentHover
                      background='bg-white'
                      styles={{
                        backgroundColor: '#fff',
                      }}
                    >
                      <span className='flex items-center gap-4 text-orange-700'>
                        {value.text} <ExleminationMark />
                      </span>
                    </Tooltip>
                  )
                ) : (
                  value
                )}
              </Typography>
            </div>
          </div>
        ))}
      </div>
      <DotedMenu<IReconciliation.IDotsMenuItem>
        list={
          data?.issuedPayment &&
          data?.totalNetPayment &&
          data?.issuedPayment === data?.totalNetPayment
            ? menuList.slice(0, 2)
            : menuList
        }
        getTitle={(item) => item.title}
        getId={(item) => item?.id}
        getAction={(item) => item.action}
        onOpen={() => setCurrentSupplierSummary()}
      />
    </div>
  );
};

export default CardContent;
